import { useState, useRef, useCallback } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ProjectBrowser } from '@/components/editor/ProjectBrowser';
import { AnimationPresets } from '@/components/editor/AnimationPresets';
import { AudioPlayer } from '@/components/editor/AudioPlayer';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Play, Pause, SkipBack, SkipForward, Undo2, Redo2,
  Save, Download, FilePlus, FolderOpen, ChevronDown,
  Sparkles, Globe, Film, Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TopBar() {
  const { state, dispatch } = useAnimation();
  const { t, lang, setLang } = useI18n();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportQuality, setExportQuality] = useState('high');
  const [exportDpi, setExportDpi] = useState(1);
  const [exportDuration, setExportDuration] = useState(null); // null = original

  const togglePlay = () => dispatch({ type: 'SET_PLAYING', payload: !state.isPlaying });
  const goToStart = () => { dispatch({ type: 'SET_PLAYING', payload: false }); dispatch({ type: 'SET_FRAME', payload: 0 }); };
  const goToEnd = () => { dispatch({ type: 'SET_PLAYING', payload: false }); dispatch({ type: 'SET_FRAME', payload: state.project.totalFrames }); };

  const handleNew = () => { dispatch({ type: 'NEW_PROJECT' }); toast.success(t('newProjectCreated')); };
  const handleLoadDemo = () => { dispatch({ type: 'LOAD_DEMO' }); toast.success(t('demoLoaded')); };
  const handleLoadDemo2 = () => { dispatch({ type: 'LOAD_DEMO_2' }); toast.success('Stickman vs. Kamera'); };
  const handleUndo = () => dispatch({ type: 'UNDO' });
  const handleRedo = () => dispatch({ type: 'REDO' });

  const handleSave = async () => {
    try {
      const projectData = {
        name: state.project.name, fps: state.project.fps,
        totalFrames: state.project.totalFrames, width: state.project.width,
        height: state.project.height, layers: state.project.layers,
      };
      if (state.project.id) {
        await axios.put(`${API}/projects/${state.project.id}`, projectData);
      } else {
        const res = await axios.post(`${API}/projects`, projectData);
        dispatch({ type: 'SET_PROJECT_PROP', payload: { id: res.data.id } });
      }
      toast.success(t('projectSaved'));
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const captureFrames = useCallback(async (quality, pixelRatio) => {
    const stepMap = { full: 1, high: 2, medium: 4, fast: 8 };
    const step = stepMap[quality] || 2;
    return new Promise((resolve) => {
      const handler = (e) => {
        window.removeEventListener('frames-captured', handler);
        resolve(e.detail);
      };
      window.addEventListener('frames-captured', handler);
      window.dispatchEvent(new CustomEvent('capture-frames', { detail: { step, pixelRatio: pixelRatio || 1 } }));
    });
  }, []);

  const handleExport = async (format) => {
    setExporting(true);
    setExportProgress(0);
    setExportDialogOpen(false);

    toast.info(t('exporting'));

    const progressHandler = (e) => setExportProgress(Math.min(e.detail, 49));
    window.addEventListener('capture-progress', progressHandler);

    try {
      const result = await captureFrames(exportQuality, exportDpi);
      window.removeEventListener('capture-progress', progressHandler);

      if (!result || !result.frames || result.frames.length === 0) {
        toast.error(t('exportFail'));
        setExporting(false);
        return;
      }

      setExportProgress(50);

      // Calculate final FPS based on desired duration
      const originalDuration = state.project.totalFrames / state.project.fps;
      const targetDuration = exportDuration || originalDuration;
      // If user wants 30s from 60s original, we need 2x the FPS
      const speedMultiplier = originalDuration / targetDuration;
      const finalFps = Math.max(1, Math.round(result.effectiveFps * speedMultiplier));

      // Chunked upload
      const CHUNK_SIZE = 30;
      const { data: { session_id } } = await axios.post(`${API}/export/start`);

      const totalFrames = result.frames.length;
      for (let i = 0; i < totalFrames; i += CHUNK_SIZE) {
        const chunk = result.frames.slice(i, i + CHUNK_SIZE);
        await axios.post(`${API}/export/chunk`, {
          session_id,
          chunk_index: Math.floor(i / CHUNK_SIZE),
          frames: chunk,
        }, { timeout: 60000 });
        setExportProgress(50 + Math.round((i / totalFrames) * 40));
      }

      setExportProgress(92);

      const res = await axios.post(`${API}/export/finish`, {
        session_id,
        fps: finalFps,
        format,
        name: state.project.name || 'animation',
      }, {
        responseType: 'blob',
        timeout: 300000,
      });

      // Check if response is actually an error (blob error responses)
      if (res.data.type === 'application/json') {
        const text = await res.data.text();
        const errData = JSON.parse(text);
        throw new Error(errData.detail || 'Export failed');
      }

      setExportProgress(100);

      const ext = format === 'mp4' ? 'mp4' : 'gif';
      const mimeType = format === 'mp4' ? 'video/mp4' : 'image/gif';
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${state.project.name || 'animation'}.${ext}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 3000);

      toast.success(t('exportDone'));
    } catch (err) {
      console.error('Export error:', err);
      const msg = err.response?.data?.detail || err.message || t('exportFail');
      toast.error(typeof msg === 'string' ? msg : t('exportFail'));
    }
    window.removeEventListener('capture-progress', progressHandler);
    setExporting(false);
  };

  const toggleLang = () => setLang(lang === 'cs' ? 'en' : 'cs');

  return (
    <div className="h-full flex items-center justify-between px-3 gap-2" data-testid="top-bar">
      {/* Left */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 mr-3">
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
            <span className="text-[8px] font-bold text-white">B</span>
          </div>
          <span className="font-display font-bold text-sm tracking-tight bg-gradient-to-r from-pink-300 to-rose-400 bg-clip-text text-transparent" data-testid="app-logo">
            Batelka
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground" data-testid="file-menu-trigger">
              {t('file')} <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={handleNew} data-testid="menu-new-project">
              <FilePlus className="w-4 h-4 mr-2" /> {t('newProject')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLoadDemo} data-testid="menu-load-demo">
              <FolderOpen className="w-4 h-4 mr-2" /> {t('loadDemo')} - Baletka
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLoadDemo2} data-testid="menu-load-demo-2">
              <FolderOpen className="w-4 h-4 mr-2" /> {t('loadDemo')} - Stickman
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSave} data-testid="menu-save">
              <Save className="w-4 h-4 mr-2" /> {t('save')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setExportDialogOpen(true)} data-testid="menu-export">
              <Download className="w-4 h-4 mr-2" /> {t('export')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={handleSave} data-testid="save-button">
          <Save className="w-3.5 h-3.5" />
        </Button>

        <ProjectBrowser
          trigger={
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" data-testid="project-browser-button">
              <FolderOpen className="w-3.5 h-3.5" />
            </Button>
          }
        />

        <AnimationPresets
          trigger={
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground" data-testid="presets-button">
              <Sparkles className="w-3 h-3" /> {t('presets')}
            </Button>
          }
        />
      </div>

      {/* Center - Playback */}
      <div className="flex items-center gap-1 bg-white/[0.03] rounded-full px-2 py-0.5" data-testid="playback-controls">
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full text-muted-foreground hover:text-foreground" onClick={handleUndo} data-testid="undo-button">
          <Undo2 className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full text-muted-foreground hover:text-foreground" onClick={handleRedo} data-testid="redo-button">
          <Redo2 className="w-3 h-3" />
        </Button>
        <div className="w-px h-4 bg-white/10 mx-1" />
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full text-muted-foreground hover:text-foreground" onClick={goToStart} data-testid="go-to-start">
          <SkipBack className="w-3 h-3" />
        </Button>
        <Button
          size="sm"
          className="h-7 w-7 p-0 rounded-full bg-rose-500 hover:bg-rose-400 text-white shadow-lg shadow-rose-500/20"
          onClick={togglePlay}
          data-testid="timeline-play-button"
        >
          {state.isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full text-muted-foreground hover:text-foreground" onClick={goToEnd} data-testid="go-to-end">
          <SkipForward className="w-3 h-3" />
        </Button>
        <span className="font-mono text-[10px] text-muted-foreground ml-2 min-w-[60px] tabular-nums">
          {state.currentFrame} / {state.project.totalFrames}
        </span>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-foreground"
          onClick={toggleLang}
          data-testid="lang-toggle"
        >
          <Globe className="w-3.5 h-3.5" />
        </Button>
        <span className="text-[9px] font-mono text-muted-foreground/50 uppercase">{lang}</span>

        <div className="w-px h-4 bg-white/10 mx-0.5" />
        <AudioPlayer />
        <div className="w-px h-4 bg-white/10 mx-0.5" />

        <Button
          size="sm"
          className="h-7 text-[11px] gap-1.5 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 border border-rose-500/20 rounded-full px-3"
          onClick={() => setExportDialogOpen(true)}
          disabled={exporting}
          data-testid="export-button"
        >
          <Download className="w-3 h-3" /> {exporting ? `${exportProgress}%` : t('export')}
        </Button>
      </div>

      {/* Export progress overlay */}
      {exporting && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center" data-testid="export-overlay">
          <div className="bg-[hsl(220,14%,13%)] border border-white/[0.08] rounded-xl p-6 w-80 space-y-4 shadow-2xl">
            <p className="text-sm font-medium text-center text-white">{t('exporting')}</p>
            <div className="w-full bg-white/[0.06] rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-rose-500 to-amber-400 rounded-full transition-all duration-300"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
            <p className="text-center text-xs font-mono text-muted-foreground tabular-nums">
              {exportProgress < 50
                ? `${t('capturingFrames') || 'Zachytavam snimky'}... ${exportProgress * 2}%`
                : exportProgress < 92
                  ? `${t('uploading') || 'Nahravani'}... ${Math.round((exportProgress - 50) / 42 * 100)}%`
                  : exportProgress < 100
                    ? `${t('encoding') || 'Kodovani'}...`
                    : `${t('exportDone')}!`
              }
            </p>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-sm bg-[hsl(220,14%,11%)] border-white/[0.08]" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-sm">{t('export')}</DialogTitle>
            <DialogDescription className="sr-only">Choose export format and quality</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {/* Quality selector */}
            <div>
              <p className="text-[10px] text-muted-foreground/60 font-mono mb-2 uppercase tracking-wider">{t('quality')}</p>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { key: 'full', label: t('qualityFull'), frames: state.project.totalFrames },
                  { key: 'high', label: t('qualityHigh'), frames: Math.ceil(state.project.totalFrames / 2) },
                  { key: 'medium', label: t('qualityMedium'), frames: Math.ceil(state.project.totalFrames / 4) },
                  { key: 'fast', label: t('qualityFast'), frames: Math.ceil(state.project.totalFrames / 8) },
                ].map(q => (
                  <button
                    key={q.key}
                    className={`px-2 py-1.5 rounded-lg text-center border transition-all ${
                      exportQuality === q.key
                        ? 'bg-rose-500/15 border-rose-500/30 text-rose-300'
                        : 'bg-white/[0.02] border-white/[0.04] text-muted-foreground hover:bg-white/[0.04]'
                    }`}
                    onClick={() => setExportQuality(q.key)}
                    data-testid={`export-quality-${q.key}`}
                  >
                    <p className="text-[10px] font-medium">{q.label}</p>
                    <p className="text-[8px] text-muted-foreground/50">{q.frames} sn.</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-white/[0.04]" />

            {/* Resolution / DPI */}
            <div>
              <p className="text-[10px] text-muted-foreground/60 font-mono mb-2 uppercase tracking-wider">{t('resolution')}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { key: 1, label: '1x', desc: `${state.project.width}x${state.project.height}` },
                  { key: 2, label: '2x', desc: `${state.project.width * 2}x${state.project.height * 2}` },
                  { key: 3, label: '3x', desc: `${state.project.width * 3}x${state.project.height * 3}` },
                ].map(r => (
                  <button
                    key={r.key}
                    className={`px-2.5 py-2 rounded-lg text-center border transition-all ${
                      exportDpi === r.key
                        ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300'
                        : 'bg-white/[0.02] border-white/[0.04] text-muted-foreground hover:bg-white/[0.04]'
                    }`}
                    onClick={() => setExportDpi(r.key)}
                    data-testid={`export-dpi-${r.key}`}
                  >
                    <p className="text-[11px] font-bold">{r.label}</p>
                    <p className="text-[8px] text-muted-foreground/50">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-white/[0.04]" />

            {/* Duration / Speed */}
            {(() => {
              const origDur = Math.round(state.project.totalFrames / state.project.fps);
              const curDur = exportDuration || origDur;
              const speed = (origDur / curDur).toFixed(1);
              return (
                <div>
                  <p className="text-[10px] text-muted-foreground/60 font-mono mb-2 uppercase tracking-wider">{t('exportDuration')}</p>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={5}
                      max={origDur * 2}
                      step={1}
                      value={curDur}
                      onChange={(e) => {
                        const v = parseInt(e.target.value);
                        setExportDuration(v === origDur ? null : v);
                      }}
                      className="flex-1 h-1.5 accent-amber-400 cursor-pointer"
                      data-testid="export-duration-slider"
                    />
                    <span className="text-[11px] font-mono text-amber-300 min-w-[32px] text-right tabular-nums" data-testid="export-duration-value">{curDur}s</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[8px] text-muted-foreground/40 font-mono">5s</span>
                    <span className="text-[9px] text-muted-foreground/50 font-mono">
                      {speed === '1.0' ? t('originalSpeed') : `${speed}x ${parseFloat(speed) > 1 ? t('faster') : t('slower')}`}
                    </span>
                    <span className="text-[8px] text-muted-foreground/40 font-mono">{origDur * 2}s</span>
                  </div>
                </div>
              );
            })()}

            <div className="h-px bg-white/[0.04]" />

            {/* Export buttons */}
            <div className="space-y-1.5">
              <button
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-rose-500/20 transition-all"
                onClick={() => handleExport('mp4')}
                disabled={exporting}
                data-testid="export-mp4-button"
              >
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 flex items-center justify-center">
                  <Film className="w-4 h-4 text-rose-400" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-medium">{t('exportMp4')}</p>
                  <p className="text-[9px] text-muted-foreground/50">Video H.264</p>
                </div>
              </button>
              <button
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-amber-500/20 transition-all"
                onClick={() => handleExport('gif')}
                disabled={exporting}
                data-testid="export-gif-button"
              >
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Image className="w-4 h-4 text-amber-400" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-medium">{t('exportGif')}</p>
                  <p className="text-[9px] text-muted-foreground/50">Animated GIF</p>
                </div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
